import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { login } from '../api/auth.js';
import { useAuthStore } from '../store/auth.store.js';
import { useThemeStore } from '../store/theme.store.js';

/* ── Brand panel (left) ──────────────────────────────────── */

function BrandPanel() {
  return (
    <div
      className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #009ee3 0%, #0062cc 60%, #003f8a 100%)' }}
    >
      {/* Geometric circles — depth */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10"
           style={{ background: 'radial-gradient(circle, white, transparent)' }} />
      <div className="absolute bottom-0 -left-16 w-72 h-72 rounded-full opacity-10"
           style={{ background: 'radial-gradient(circle, white, transparent)' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-5"
           style={{ background: 'radial-gradient(circle, white, transparent)' }} />

      {/* Top logo — texto blanco explícito para fondo azul */}
      <div className="relative z-10">
        <img src="/logo-dark.svg" alt="McaMP" className="h-14 xl:h-16" />
      </div>

      {/* Center copy */}
      <div className="relative z-10 space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-white leading-tight">
            Gateway<br />MercadoPago<br />Point
          </h1>
          <p className="mt-4 text-blue-100 text-base leading-relaxed max-w-xs">
            Administración multi-tenant de terminales físicas, órdenes de cobro y automatización de tokens OAuth.
          </p>
        </div>

        {/* Feature pills */}
        <div className="flex flex-wrap gap-2">
          {['Multi-tenant', 'PDV integrado', 'Webhooks', 'OAuth 2.0'].map((f) => (
            <span key={f}
              className="text-xs px-3 py-1 rounded-full border border-white/30 text-white/80 bg-white/10 backdrop-blur">
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom */}
      <div className="relative z-10">
        <p className="text-xs text-blue-200/60">© {new Date().getFullYear()} MCAnet · mp-pos-api</p>
      </div>
    </div>
  );
}

/* ── Login form (right) ──────────────────────────────────── */

export default function Login() {
  const navigate        = useNavigate();
  const location        = useLocation();
  const [searchParams]  = useSearchParams();
  const setSession      = useAuthStore((s) => s.setSession);
  const activated       = searchParams.get('activated') === '1';

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState(null);

  const { darkMode } = useThemeStore();

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      setSession({ user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken });
      if (data.user.forcePasswordChange) {
        navigate('/activate', { replace: true });
      } else {
        navigate(location.state?.from?.pathname || '/', { replace: true });
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || err.response?.data?.error || 'Credenciales inválidas');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    mutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: 'var(--bg)' }}>

      <BrandPanel />

      {/* Form side */}
      <div className="flex flex-col items-center justify-center px-8 py-12">

        {/* Mobile-only logo — cambia según el tema */}
        <div className="mb-10 lg:hidden">
          <img
            src={darkMode ? '/logo-dark.svg' : '/logo-light.svg'}
            alt="McaMP"
            className="h-14 sm:h-16"
          />
        </div>

        <div className="w-full max-w-sm">

          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Bienvenido
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              Ingresá tus credenciales para continuar
            </p>
          </div>

          {/* Activation success banner */}
          {activated && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-emerald-400
                            bg-emerald-400/10 border border-emerald-400/20 mb-6">
              <CheckCircle2 size={15} className="shrink-0" />
              Cuenta activada correctamente. Ya podés ingresar con tu nueva contraseña.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                required
                placeholder="usuario@mcanet.com.ar"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl text-sm border transition-all
                           focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                style={{
                  background:   'var(--input-bg)',
                  borderColor:  'var(--border-strong)',
                  color:        'var(--text-primary)',
                }}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-11 rounded-xl text-sm border transition-all
                             focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                  style={{
                    background:  'var(--input-bg)',
                    borderColor: 'var(--border-strong)',
                    color:       'var(--text-primary)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-red-400
                              bg-red-400/10 border border-red-400/20">
                <AlertCircle size={15} className="shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!email || !password || mutation.isPending}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl
                         text-sm font-semibold text-white transition-all
                         disabled:opacity-50 disabled:cursor-not-allowed
                         hover:brightness-110 active:scale-[0.98]"
              style={{ background: 'rgb(var(--accent-rgb))' }}
            >
              {mutation.isPending
                ? <><Loader2 size={16} className="animate-spin" />Ingresando…</>
                : 'Ingresar'}
            </button>

          </form>

          {/* Footer */}
          <p className="text-xs text-center mt-8" style={{ color: 'var(--text-muted)' }}>
            McaMP · Panel de administración ·{' '}
            <a
              href="/docs/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--text-muted)' }}
              className="hover:underline"
            >
              Documentación
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
